import { useState } from 'react';
import { Modal } from 'src/ui/Modal';
import { Button } from 'src/ui/Button';
import { UserPickerPanel } from 'src/ui/patterns/UserPickerPanel';
import { useAddEventUserMutation } from 'src/store/eventApi';
import { UserEntity } from 'src/store/userApi';

interface AddAttendeesModalProps {
  eventId: string;
  excludeUserIds: string[];
  isOpen: boolean;
  onClose: () => void;
}

const AddAttendeesModal = ({
  eventId,
  excludeUserIds,
  isOpen,
  onClose,
}: AddAttendeesModalProps) => {
  const [staged, setStaged] = useState<Map<string, UserEntity>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addEventUser] = useAddEventUserMutation();

  const reset = () => {
    setStaged(new Map());
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const toggleUser = (user: UserEntity | null) => {
    if (!user) return;
    setStaged((prev) => {
      const next = new Map(prev);
      if (next.has(user.id)) next.delete(user.id);
      else next.set(user.id, user);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (staged.size === 0) return;
    setIsSubmitting(true);
    try {
      await Promise.all(
        Array.from(staged.values()).map((u) =>
          addEventUser({ eventId, userId: u.id }).unwrap()
        )
      );
      reset();
      onClose();
    } catch (err) {
      setIsSubmitting(false);
    }
  };

  const stagedCount = staged.size;
  const stagedNames = Array.from(staged.values())
    .map((u) => u.profile?.name || u.email || u.id)
    .join(', ');

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Add attendees"
      subtitle="Search users to add to this event."
      width={720}
      footer={
        <div className="row gap-3" style={{ alignItems: 'center' }}>
          <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>
            {stagedCount} selected
          </span>
          {stagedCount > 0 && (
            <span
              className="text-muted truncate"
              style={{ fontSize: 'var(--fs-xs)', flex: 1 }}
            >
              {stagedNames}
            </span>
          )}
          <div style={{ flex: stagedCount > 0 ? 0 : 1 }} />
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon="plus"
            onClick={handleConfirm}
            disabled={stagedCount === 0}
            loading={isSubmitting}
          >
            Add {stagedCount > 0 ? stagedCount : ''} user
            {stagedCount === 1 ? '' : 's'}
          </Button>
        </div>
      }
    >
      <div
        style={{ height: 'min(60vh, calc(100vh - 11rem))', overflow: 'hidden' }}
      >
        <UserPickerPanel
          multiSelect
          selectedUserIds={Array.from(staged.keys())}
          onSelect={toggleUser}
          excludeUserIds={excludeUserIds}
          title="Users"
          height="100%"
        />
      </div>
    </Modal>
  );
};

export default AddAttendeesModal;
